import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";

const ScrollIndicator = ({
  scrollContainerRef,
  showDelay = 500, // time to remain visible after scrolling stops
  hideDelay = 1000, // reduced time before hiding after inactivity
  color = "white", // color of the indicator
}) => {
  const [, setScrollPercentage] = useState(0);
  const [, setIsScrolling] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // Start hidden by default
  const [, setHasUserScrolled] = useState(false); // Track if user has scrolled
  const [canScroll, setCanScroll] = useState(false); // Track if container is scrollable
  const scrollTimeoutRef = useRef(null);
  const visibilityTimeoutRef = useRef(null);
  const [indicatorHeight, setIndicatorHeight] = useState(30); // Default height
  const [indicatorTop, setIndicatorTop] = useState(0);

  // Initialize and cleanup the scroll handler
  useEffect(() => {
    // Calculate scroll percentage and handle visibility
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;
      const scrollableHeight = scrollHeight - clientHeight;

      // Only show scroll indicator if content exceeds container height
      if (scrollableHeight <= 0) {
        setCanScroll(false);
        setIsVisible(false);
        return;
      }

      setCanScroll(true);
      setHasUserScrolled(true);

      // Calculate percentage and positions
      const percentage = (scrollTop / scrollableHeight) * 100;
      setScrollPercentage(percentage);

      // Calculate the height of the scroll indicator
      // Minimum height is 30px, regardless of content
      const containerHeight = clientHeight;
      const contentHeight = scrollHeight;
      const newHeight = Math.max(
        30,
        (containerHeight / contentHeight) * containerHeight
      );
      const newTop = (percentage / 100) * (containerHeight - newHeight);

      setIndicatorHeight(newHeight);
      setIndicatorTop(newTop);

      // Clear previous timeouts
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (visibilityTimeoutRef.current)
        clearTimeout(visibilityTimeoutRef.current);

      // Set scrolling state to true and make indicator visible
      setIsScrolling(true);
      setIsVisible(true);

      // Set timeout to handle when scrolling stops
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);

        // Hide the indicator after a delay
        visibilityTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, hideDelay);
      }, showDelay);
    };

    // Check if container is scrollable on resize or content change
    const checkScrollability = () => {
      if (!scrollContainerRef.current) return;

      const { scrollHeight, clientHeight } = scrollContainerRef.current;
      const isScrollable = scrollHeight > clientHeight;

      setCanScroll(isScrollable);

      // If not scrollable, ensure indicator is hidden
      if (!isScrollable) {
        setIsVisible(false);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    // Initial check if scrolling is possible
    checkScrollability();

    // Observe content changes and resize
    const resizeObserver = new ResizeObserver(() => {
      checkScrollability();
    });

    resizeObserver.observe(scrollContainer);

    // Add scroll event listener
    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    // Clean up
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (visibilityTimeoutRef.current)
        clearTimeout(visibilityTimeoutRef.current);
      scrollContainer.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [hideDelay, scrollContainerRef, showDelay]);

  // Don't render anything if there's no scrollable content or visibility flag
  if (!canScroll || !isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }} // 50% opacity
          exit={{ opacity: 0 }}
          className="absolute right-0 h-full z-50 pointer-events-none" // Positioned flush with right edge
          style={{
            top: 0,
            width: "8px",
          }}
        >
          <motion.div
            className="absolute rounded-full"
            style={{
              width: "100%",
              height: `${indicatorHeight}px`,
              backgroundColor: color,
              top: `${indicatorTop}px`,
              right: 0,
              boxShadow: "0 0 4px rgba(255, 255, 255, 0.5)", // Added subtle glow
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScrollIndicator;
